<template>
  <div class="setup_step card">
    <h4>
      Set up your custom domain
    </h4>

    <p class="step-description">
      To get started with the Merchant Centre, we first need you to set-up a custom domain to host your white-label
      instance of Hike. Most customers choose a sub-domain on their agency website domain. For example,
      seo.yourdomain.com or platform.yourdomain.com.
    </p>

    <p class="step-description">
      Click the button below to discover the steps you need to take to set this up.
    </p>

    <button
      class="button info"
      @click="showInstructionsModal = true;"
    >
      <svg-vue icon="icons/question_mark_in_circle" />
      How to setup your custom domain?
    </button>

    <SetupDomainModal
      v-if="showInstructionsModal"
      @close="showInstructionsModal = false;"
    />

    <div class="form-group">
      <label>Custom Domain</label>
      <input
        v-model="customDomain"
        type="text"
        :class="[
          'form-control',
          errors.custom_domain ? 'error' : '',
        ]"
        placeholder="www.custom-domain.com"
      >

      <ErrorMessages
        v-if="errors?.custom_domain"
        :errors="errors.custom_domain"
      />

      <small>
        Most customers choose a sub-domain on their agency website domain. For example, seo.yourdomain.com or
        platform.yourdomain.com
      </small>
    </div>

    <div v-if="whitelabelStatus.customDomainStatus === 'ERROR'">
      <div class="row no-gutters">
        <div class="col-12 col-lg-6 pr-lg-2">
          <button
            class="button flex-grow-1 w-100 mt-0"
            :disabled="loading || customDomainUnchanged"
            @click="submitCustomDomain"
          >
            <i
              v-if="loading"
              class="fas fa-spin fa-spinner"
            />
            Update
          </button>
        </div>
        <div class="col-12 col-lg-6 mt-3 mt-lg-0 pl-lg-2">
          <button
            class="button error flex-grow-1 w-100 mt-0"
            :disabled="loading"
            @click="reVerify"
          >
            <i
              v-if="loading"
              class="fas fa-spin fa-spinner"
            />
            Re-Verify
          </button>
        </div>
      </div>
      <div
        v-if="whitelabelCustomDomainMessage"
        class="alert-danger align-items-lg-start border-radius-6 d-flex p-3 text-danger mt-3"
      >
        <svg-vue
          class="svg mr-2 svg-32"
          icon="icons/warning-red"
          alt="warning"
        />
        <span>
          {{ whitelabelCustomDomainMessage }}
        </span>
      </div>
    </div>

    <div v-else>
      <button
        class="button w-100 mt-0"
        :disabled="loading"
        @click="submitCustomDomain"
      >
        <i
          v-if="loading"
          class="fas fa-spin fa-spinner"
        />
        Continue
      </button>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import axios from 'axios';
  import Swal from 'sweetalert2';
  import SetupDomainModal from "../../whitelabel/SetupDomainModal.vue";
  import ErrorMessages from "../../global/ErrorMessages.vue";

  export default {
    components: {
      ErrorMessages,
      SetupDomainModal,
    },
    data() {
      return {
        showInstructionsModal: false,
        errors: {},
        initialCustomDomain: '',
      };
    },
    computed: {
      ...mapGetters('merchantCentreSetupWizard', [
        'formFields',
        'loading',
        'whitelabelStatus',
        'whitelabelCustomDomainMessage',
      ]),
      customDomain: {
        get() {
          return this.formFields.customDomain;
        },
        set(customDomain) {
          this.setFormField({customDomain});
        }
      },
      customDomainUnchanged() {
        return this.initialCustomDomain === this.customDomain;
      },
    },
    async mounted() {
      await this.fetchWhitelabelStatus();

      if (this.whitelabelStatus.customDomainStatus === 'ERROR') {
        await this.fetchWhitelabelCustomDomainMessage();
      }

      this.initialCustomDomain = this.customDomain;
    },
    methods: {
      ...mapActions('merchantCentreSetupWizard', [
          'setCurrentStep',
          'setFormField',
          'setLoadingState',
          'fetchWhitelabelStatus',
          'fetchWhitelabelCustomDomainMessage',
      ]),
      async submitCustomDomain() {
        this.setLoadingState(true);
        await axios.post('/merchant/setup-custom-domain', {
            custom_domain: this.customDomain
          })
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: 'Custom domain setup requested, please allow 5-10 minutes for the process to complete',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });

            this.setCurrentStep('CUSTOM_DOMAIN');
          })
          .catch(
            (e) => this.$axiosExceptionHandler(e, ({ response }) => {
              if (response.status === 422) {
                const {errors} = response.data;
                this.errors = errors;
              } else {
                const { data } = response;
                Swal.fire({
                  title: "Whoops",
                  html: data.message ?? 'Something went wrong, please try again later.',
                  icon: 'warning',
                });
              }
            })
          )
          .finally(() => {
            this.setLoadingState(false);
          })
      },
      async reVerify() {
        this.setLoadingState(true);
        await axios.get('/merchant/re-verify-custom-domain')
          .then((response) => {
            if(response.data.custom_domain_status === 'ERROR') {
              this.fetchWhitelabelCustomDomainMessage();
              return;
            }

            Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: 'Custom domain is all setup',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });

          this.setCurrentStep('CUSTOM_DOMAIN');
          })
          .catch(
            (e) => this.$axiosExceptionHandler(e, ({ response }) => {
              if (response.status === 422) {
                const {errors} = response.data;
                this.errors = errors;
              } else {
                const { data } = response;
                Swal.fire({
                  title: "Whoops",
                  html: data.message ?? 'Something went wrong, please try again later.',
                  icon: 'warning',
                });
              }
            })
          )
          .finally(() => {
            this.setLoadingState(false);
          })
      },
    }
  }
</script>

<style lang="scss" scoped>
  .button {
    margin-top: 1rem;

    &.info {
      margin: 0;
    }
  }
  .svg-32 {
    max-width: 32px !important;
  }
</style>
